import { z } from "zod";

/**
 * Примитивы контрактов. Соглашения (rules/schema-first.md §2):
 * ID — UUID v7, время — ISO-строка, локаль — TEXT ISO 639-1, деньги — minor units.
 */

/** UUID v7 — время-упорядоченный. Генерирует приложение (пакет `uuidv7`). */
export const UuidV7Schema = z.uuid();

/** ISO-8601 с таймзоной. Date-объекты в JSON не ходят. */
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });

/** Дата без времени (дата рождения, дата сгорания). */
export const IsoDateSchema = z.iso.date();

/**
 * Локаль — двухбуквенный код ISO 639-1 в TEXT.
 *
 * НИКОГДА не enum, и число тут намеренно не названо: в CLAUDE.md было «15
 * локалей», в `ECOSYSTEM_LOCALES` их 21, и разошлись они молча. Закрытый
 * список живёт в ОДНОМ месте — `constants/locales.ts`; здесь проверяется
 * только форма кода, потому что схема применяется в том числе к значениям,
 * пришедшим извне (`Accept-Language` браузера, профиль стороннего сервиса),
 * и отвергать чужой валидный ISO-код мы не вправе. См. `rules/i18n.md`.
 */
export const LocaleSchema = z.string().regex(/^[a-z]{2}$/, "Локаль — код ISO 639-1");

/** Валюта расчётов. Цены экосистемы — только USD; остальные для отображения. */
export const CurrencySchema = z.enum(["USD", "EUR", "KZT", "RUB"]);

/** Деньги — целое в минимальных единицах (центы). Float запрещён. */
export const MoneyMinorSchema = z.number().int();

/** Баллы — всегда целые. Знак несёт направление операции. */
export const PointsAmountSchema = z.number().int();

/** Идентификатор сервиса экосистемы. TEXT, а не enum — сервисы добавляются. */
export const ServiceIdSchema = z.string().min(2).max(64);

/** `sub` — ключ связи между сервисами. Значения наследуются от старого SSO. */
export const SubjectSchema = z.string().min(1).max(64);

/** Курсор пагинации. Offset-пагинация запрещена (нестабильна при записи). */
export const CursorSchema = z.string().nullish();

export const PaginationInputSchema = z.object({
	cursor: CursorSchema,
	limit: z.number().int().min(1).max(100).default(20),
});

export function paginated<T extends z.ZodTypeAny>(item: T) {
	return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

/** Маскированная почта для экранов, где полную показывать нельзя (chooser). */
export const MaskedEmailSchema = z.string().min(3);

export const EmailSchema = z.email().max(320).toLowerCase();

export type Locale = z.infer<typeof LocaleSchema>;
export type Currency = z.infer<typeof CurrencySchema>;
export type ServiceId = z.infer<typeof ServiceIdSchema>;
export type Subject = z.infer<typeof SubjectSchema>;
